import type { CurrencySymbol } from '@/core/tamagoshi/deprecated/PriceDeprecated/utils/currency';
import { translate, translateFormatter } from '@/domains/core/localization/translate';
import { isB2B, isFrancePlatform, isUkPlatform } from '@/domains/core/settings/utils';
import { productLabels } from '@/domains/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/interfaces';
import type { FilterConfig } from '@/productDiscovery/Listings/interfaces/filterConfig';
import type { ListingPage } from '@/productDiscovery/Listings/model';

// NOTE Filters configuration file
//      Use `getGenericFiltersConfig` as default/fallback configuration (or each generic methods for specific pages)
//      Use `getAllGenericFiltersConfig` as a library of the full filters list
//      ---
//      All filters `type` can be used for Algolia's facets, except `custom` which is specific
//      to define your own filter condition (not include in facets). For this type there are
//      two more props:
//      - condition:string (mandatory): filter condition added to the filters searchParameters
//      - tagName:string (optional): used to display a tag after the filter label

const deliveryFilters: FilterConfig[] = [{
  attributeName: 'is_mmf',
  urlKey: 'mmf',
  translations: {
    ...translate({
      "title": "Shipping method"
    }),
    label: {
      id: 'listing.filters.facets.mmf.label',
      defaultMessage: 'By ManoMano {logo}',
      description: 'Title of bingo filter'
    }
  },
  type: 'bool'
}, {
  attributeName: 'has_free_delivery',
  urlKey: 'fd',
  translations: {
    ...translate({
      "label": "Free delivery"
    })
  },
  type: 'bool'
}, {
  attributeName: 'has_1day_delivery',
  urlKey: '1dd',
  translations: {
    ...translate({
      "label": "1 day delivery"
    })
  },
  type: 'bool'
}, {
  attributeName: 'has_relay_delivery',
  urlKey: 'rd',
  translations: {
    ...translate({
      "label": "Delivery to a collection point"
    })
  },
  type: 'bool'
}];
const priceFilter: FilterConfig = {
  attributeName: 'price',
  urlKey: 'price',
  translations: {
    label: translateFormatter<{
      currency: CurrencySymbol;
    }>([{
      "type": 0,
      "value": "Price ("
    }, {
      "type": 1,
      "value": "currency"
    }, {
      "type": 0,
      "value": ")"
    }], undefined, undefined, "en").format({
      currency: isUkPlatform() ? '£' : '€'
    }) as string
  },
  type: 'range'
};
const brandNameFilter: FilterConfig = {
  attributeName: 'brand_name',
  urlKey: 'brand_names',
  translations: {
    ...translate({
      "label": "Brands",
      "subTitle": "All brands"
    })
  },
  type: 'checkbox'
};
const ratingsFilter: FilterConfig = {
  attributeName: 'rating_rounded_down',
  urlKey: 'rating',
  translations: {
    ...translate({
      "label": "Reviews"
    })
  },
  type: 'checkbox'
};

// This default filter should always be in last position
const sellerFilter: FilterConfig = {
  attributeName: 'seller_name',
  urlKey: 'seller_names',
  translations: {
    ...translate({
      "label": "Sellers"
    })
  },
  type: 'checkbox'
};
export const localFilter: FilterConfig = {
  attributeName: 'is_local',
  urlKey: 'mi',
  translations: {
    ...translate({
      "label": "Made in the UK"
    })
  },
  type: 'bool'
};
export const ecoResponsibleFilter: FilterConfig = {
  attributeName: 'is_eco_responsible',
  hiddenWhenNoResults: true,
  urlKey: 'ec',
  translations: {
    ...translate({
      "label": "Eco responsible"
    })
  },
  type: 'bool'
};
export const colorsFilter: FilterConfig = {
  attributeName: 'color',
  urlKey: 'colors',
  translations: {
    ...translate({
      "label": "Colours"
    })
  },
  type: 'color'
};
const b2bFilter: FilterConfig = {
  attributeName: 'has_pro_conditions',
  urlKey: 'pro',
  translations: {
    ...translate({
      "label": "Products reserved for pros",
      "title": "Catalog"
    })
  },
  type: 'bool'
};
export const advantagesFilter: FilterConfig = {
  attributeName: 'promotions',
  urlKey: 'bf',
  translations: {
    ...translate({
      "label": "Deals"
    })
  },
  tagName: productLabels.PROMO,
  condition: 'discount > 0 AND commercial_animation.tags:promo',
  type: 'custom'
};

// This CategoryLastFilter should always in 1st position
export const categoryLastFilterUrlKey = 'category_names';
export const categoryLastFilter: FilterConfig = {
  attributeName: 'categories.last',
  urlKey: categoryLastFilterUrlKey,
  translations: {
    ...translate({
      "label": "Categories"
    })
  },
  type: 'checkbox'
};
export const getAllGenericFiltersConfig = (): FilterConfig[] => {
  const filtersConfig = [advantagesFilter, priceFilter, brandNameFilter, sellerFilter, b2bFilter, categoryLastFilter, localFilter, ecoResponsibleFilter, ...deliveryFilters];
  if (!isB2B()) {
    filtersConfig.push(ratingsFilter);
  }
  return filtersConfig;
};
export interface GenericFiltersPageOptions {
  commercialOperationCategory?: string;
}
interface GetGenericFiltersConfigParam {
  pageName?: ListingPage;
  pageOptions?: GenericFiltersPageOptions;
}

// Generic fiters configuration for static or backup configuration
export const getGenericFiltersConfig = ({
  pageName,
  pageOptions
}: GetGenericFiltersConfigParam = {}): FilterConfig[] => {
  const filtersConfig = [priceFilter, brandNameFilter, ...deliveryFilters];
  if (isB2B()) {
    filtersConfig.push(b2bFilter);
  } else {
    filtersConfig.splice(1, 0, ratingsFilter);
  }
  if (pageName === 'commercialOperationListing') {
    filtersConfig.unshift(categoryLastFilter);
  }
  if (pageName === 'commercialOperationListing' && pageOptions?.commercialOperationCategory) {
    filtersConfig.push(generateCommercialOperationCategoryFilterConfig(pageOptions?.commercialOperationCategory));
  }
  filtersConfig.push(sellerFilter);
  if (isFrancePlatform()) {
    filtersConfig.push(ecoResponsibleFilter);
    filtersConfig.push(localFilter);
  }
  return filtersConfig;
};

// Commercial Operation listing filters configuration
const generateCommercialOperationCategoryFilterConfig = (categoryAttributeName: string): FilterConfig => ({
  attributeName: categoryAttributeName,
  urlKey: 'category_id',
  type: 'menu',
  translations: {
    label: ''
  }
});

// Brand listing filters configuration
export const getBrandStoreFiltersConfig = (): FilterConfig[] => {
  const filters = [advantagesFilter, categoryLastFilter, {
    ...priceFilter,
    attributeName: 'prices.per_item.actual_price.with_vat.as_float'
  }, ratingsFilter, sellerFilter, ...deliveryFilters];

  // Add some filters only for FR platform at the end
  if (isFrancePlatform()) {
    filters.push(ecoResponsibleFilter);
    filters.push(localFilter);
  }
  return filters;
};